import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Button, InlineAlert } from '@chillout/ui'
import { AdminModal, ModalActions } from '../shared/Modal'
import type { AppSettings } from './useAppSettings'

interface SettingsSaveConfirmModalProps {
  visible: boolean
  savedSettings: AppSettings
  pendingSettings: AppSettings
  saving: boolean
  error: boolean
  onConfirm: () => void
  onCancel: () => void
}

interface ChangeRowProps {
  label: string
  before: string
  after: string
}

function ChangeRow({ label, before, after }: ChangeRowProps) {
  return (
    <View className="rounded-lg border border-border bg-canvas-primary px-4 py-3">
      <Text className="text-xs font-semibold uppercase tracking-wide text-secondary">{label}</Text>
      <View className="mt-1 flex-row items-center gap-2">
        <Text className="text-sm text-secondary" numberOfLines={1}>{before}</Text>
        <Text className="text-sm text-muted">&rarr;</Text>
        <Text className="text-sm font-medium text-primary" numberOfLines={1}>{after}</Text>
      </View>
    </View>
  )
}

export function SettingsSaveConfirmModal({
  visible,
  savedSettings,
  pendingSettings,
  saving,
  error,
  onConfirm,
  onCancel,
}: SettingsSaveConfirmModalProps) {
  const { t } = useTranslation()

  const onLabel = t('common.on')
  const offLabel = t('common.off')

  const changes: ChangeRowProps[] = []

  if (pendingSettings.maxStamps !== savedSettings.maxStamps) {
    changes.push({
      label: t('settings.saveConfirm.maxStampsPerTransaction'),
      before: String(savedSettings.maxStamps),
      after: String(pendingSettings.maxStamps),
    })
  }

  if (pendingSettings.receiptReferenceEnabled !== savedSettings.receiptReferenceEnabled) {
    changes.push({
      label: t('settings.saveConfirm.receiptReference'),
      before: savedSettings.receiptReferenceEnabled ? onLabel : offLabel,
      after: pendingSettings.receiptReferenceEnabled ? onLabel : offLabel,
    })
  }

  if (pendingSettings.referralProgramEnabled !== savedSettings.referralProgramEnabled) {
    changes.push({
      label: t('settings.saveConfirm.referralProgram'),
      before: savedSettings.referralProgramEnabled ? onLabel : offLabel,
      after: pendingSettings.referralProgramEnabled ? onLabel : offLabel,
    })
  }

  if (pendingSettings.cashierStampCorrectionEnabled !== savedSettings.cashierStampCorrectionEnabled) {
    changes.push({
      label: t('settings.saveConfirm.cashierStampCorrection'),
      before: savedSettings.cashierStampCorrectionEnabled ? onLabel : offLabel,
      after: pendingSettings.cashierStampCorrectionEnabled ? onLabel : offLabel,
    })
  }

  if (pendingSettings.cashierRewardRestoreEnabled !== savedSettings.cashierRewardRestoreEnabled) {
    changes.push({
      label: t('settings.saveConfirm.cashierRewardRestore'),
      before: savedSettings.cashierRewardRestoreEnabled ? onLabel : offLabel,
      after: pendingSettings.cashierRewardRestoreEnabled ? onLabel : offLabel,
    })
  }

  return (
    <AdminModal
      visible={visible}
      onClose={onCancel}
      closeAccessibilityLabel={t('common.cancel')}
      title={t('settings.saveConfirm.title')}
      subtitle={t('settings.saveConfirm.subtitle')}
      actions={
        <ModalActions>
          <Button
            label={t('common.cancel')}
            variant="secondary"
            className="flex-1"
            onPress={onCancel}
            disabled={saving}
          />
          <Button
            label={t('settings.saveConfirm.confirm')}
            loadingLabel={t('settings.saveConfirm.saving')}
            className="flex-1"
            loading={saving}
            onPress={onConfirm}
          />
        </ModalActions>
      }
    >
      <View className="mt-5 gap-3">
        {changes.map((change) => (
          <ChangeRow key={change.label} {...change} />
        ))}
        {error ? <InlineAlert message={t('settings.saveConfirm.saveError')} /> : null}
      </View>
    </AdminModal>
  )
}